<script lang="ts" setup>
import { ref, computed, watch } from 'vue';
import { useRouter } from 'vue-router';
import { formatDate } from '@/utils/date-utils';
import { useQuery } from '@vue/apollo-composable';
import { UsersDocument, UserType } from '@/gql/graphql';
import { getActiveUserOrganizationAssociation } from '@/utils/user-utils';
import {
  BAlert,
  BButton,
  BCard,
  BForm,
  BFormGroup,
  BFormInput,
  BPagination,
  BSpinner,
  BTable,
} from 'bootstrap-vue-next';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { deepParseDates, formatDateTimeStringToIso } from '@/utils/date-utils';
import { PAGING_ITEMS_PER_PAGE } from '@/constants';
import { openRouterLink } from '@/utils/router-utils';

const router = useRouter();

// Filtering and paging state
const id = ref<number | null>(null);
const uuid = ref<string | null>(null);
const givenNamePattern = ref<string | null>(null);
const familyNamePattern = ref<string | null>(null);
const emailPattern = ref<string | null>(null);
const strCreatedAfter = ref<string | null>(null);
const strCreatedBefore = ref<string | null>(null);
const dtCreatedAfter = ref<string | null>(null);
const dtCreatedBefore = ref<string | null>(null);

const pagingPageCurrent = ref(1);
const pagingItemsPerPage = ref(PAGING_ITEMS_PER_PAGE);
const pagingTotalRows = computed(() => {
  return usersQueryResult.value?.users?.total || 0;
});

const users = computed<UserType[]>(() => {
  return (
    usersQueryResult.value?.users?.users
      ?.filter((user) => user && user !== null)
      .map((user) => ({
        ...(deepParseDates(user) as UserType),
      })) || []
  );
});

// GraphQL Query to Get Users
const {
  result: usersQueryResult,
  loading: usersQueryInProgress,
  error: usersQueryError,
  refetch: usersQueryRefetch,
} = useQuery(UsersDocument, {
  id: id.value,
  uuid: uuid.value,
  givenNamePattern: givenNamePattern.value,
  familyNamePattern: familyNamePattern.value,
  emailPattern: emailPattern.value,
  createdAfter: dtCreatedAfter.value,
  createdBefore: dtCreatedBefore.value,
  pagingPageCurrent: pagingPageCurrent.value,
  pagingItemsPerPage: pagingItemsPerPage.value,
});

function clearFilter() {
  id.value = null;
  uuid.value = null;
  givenNamePattern.value = '';
  familyNamePattern.value = '';
  emailPattern.value = '';
  strCreatedAfter.value = null;
  strCreatedBefore.value = null;
  dtCreatedAfter.value = null;
  dtCreatedBefore.value = null;
  pagingPageCurrent.value = 1;

  requestRefetchUsers();
}

// Function to refetch data
function requestRefetchUsers() {
  // check integer values are integer values
  if (!Number.isInteger(id.value)) {
    console.warn('Invalid user ID. Resetting to null.');
    id.value = null;
  }

  // convert date and time to dateTime object (current timezone)
  dtCreatedAfter.value = formatDateTimeStringToIso(strCreatedAfter.value, null);
  dtCreatedBefore.value = formatDateTimeStringToIso(
    strCreatedBefore.value,
    null
  );

  // refetch the query
  usersQueryInProgress.value = true;
  usersQueryError.value = null;

  // refetch the query with the new variables
  usersQueryRefetch({
    id: id.value,
    uuid: uuid.value,
    givenNamePattern: givenNamePattern.value,
    familyNamePattern: familyNamePattern.value,
    emailPattern: emailPattern.value,
    createdAfter: dtCreatedAfter.value,
    createdBefore: dtCreatedBefore.value,
    pagingPageCurrent: pagingPageCurrent.value,
    pagingItemsPerPage: pagingItemsPerPage.value,
  })
    ?.catch((err) => {
      console.error('Refetch failed:', err);
      usersQueryError.value = err;
      usersQueryInProgress.value = false;
    })
    .then(() => {
      console.log('refetched');
      usersQueryInProgress.value = false;
    });
}

function onSearchRequested() {
  if (pagingPageCurrent.value !== 1) {
    pagingPageCurrent.value = 1;
  } else {
    requestRefetchUsers();
  }
}

const fields = [
  {
    key: 'createdAt',
    label: 'Created',
    sortable: true,
    formatter: formatDate,
    tdClass: 'align-middle',
  },
  {
    key: 'givenName',
    label: 'Given Name',
    sortable: true,
    tdClass: 'align-middle',
  },
  {
    key: 'familyName',
    label: 'Family Name',
    sortable: true,
    tdClass: 'align-middle',
  },
  {
    key: 'email',
    label: 'E-mail',
    sortable: true,
    tdClass: 'align-middle',
  },
  {
    key: 'organization',
    label: 'Organization',
    sortable: false,
    tdClass: 'align-middle',
  },
  {
    key: 'id',
    label: '',
    tdClass: 'align-middle text-end min-width',
  },
];

watch(pagingPageCurrent, (newPage) => {
  console.debug('page changed to', newPage);
  requestRefetchUsers();
});

function handleUserDetailRequest(
  user: UserType,
  index?: number,
  evt?: MouseEvent
) {
  if (user && user.id) {
    openRouterLink(
      {
        name: 'AdminUserDetail',
        params: { id: user.id },
      },
      router,
      evt
    );
  }
}
</script>

<template>
  <div>
    <!-- Users Title -->
    <h1><font-awesome-icon :icon="['fas', 'users']" /> Users</h1>
    <h5>Browse registered users</h5>
    <hr />
    <!-- Content -->
    <div>
      <!-- Query Error -->
      <b-alert
        v-model="usersQueryError"
        variant="danger"
        dismissible
        class="mt-3"
      >
        {{ usersQueryError }}
        <b-button
          @click="requestRefetchUsers"
          variant="outline-dark"
          class="btn-sm"
        >
          <font-awesome-icon :icon="['fas', 'sync']" />
          Retry</b-button
        >
      </b-alert>

      <!-- Filter -->
      <div class="my-3">
        <BForm
          class="d-flex flex-row align-items-end flex-wrap gap-2"
          @submit.prevent="onSearchRequested"
        >
          <BFormGroup label="User ID" label-for="user-id">
            <BFormInput
              id="user-id"
              type="number"
              v-model.number="id"
              placeholder="User ID"
            />
          </BFormGroup>
          <BFormGroup label="User UUID" label-for="uuid">
            <BFormInput
              id="uuid"
              type="text"
              v-model="uuid"
              placeholder="e.g. 98a21803-733c-4323-886e-257ba4c27f5e"
            />
          </BFormGroup>
          <BFormGroup label="Given name" label-for="given-name">
            <BFormInput
              id="given-name"
              type="text"
              v-model="givenNamePattern"
              placeholder="e.g. John"
            />
          </BFormGroup>
          <BFormGroup label="Family name" label-for="family-name">
            <BFormInput
              id="family-name"
              type="text"
              v-model="familyNamePattern"
              placeholder="e.g. Doe"
            />
          </BFormGroup>
          <BFormGroup label="E-mail" label-for="email">
            <BFormInput
              id="email"
              type="text"
              v-model="emailPattern"
              placeholder="e.g. joe@email.com"
            />
          </BFormGroup>
          <BFormGroup
            label="Registered after"
            label-for="date-registered-after"
          >
            <BFormInput
              id="date-registered-after"
              type="date"
              v-model="strCreatedAfter"
            />
          </BFormGroup>
          <BFormGroup
            label="Registered before"
            label-for="date-registered-before"
          >
            <BFormInput
              id="date-registered-before"
              type="date"
              v-model="strCreatedBefore"
            />
          </BFormGroup>
          <!-- Filter action buttons -->
          <BButton variant="outline-secondary" @click="clearFilter">
            <font-awesome-icon icon="trash" class="me-1" />
            Clear filter
          </BButton>
          <BButton
            variant="outline-dark"
            type="submit"
            :disabled="usersQueryInProgress"
          >
            <span v-if="usersQueryInProgress">
              <b-spinner small label=""></b-spinner>
              Searching
            </span>
            <span v-else>
              <font-awesome-icon icon="magnifying-glass" class="me-1" />
              Refresh
            </span>
          </BButton>
        </BForm>
      </div>

      <!-- Paging (above) -->
      <BPagination
        v-if="pagingTotalRows > pagingItemsPerPage"
        v-model="pagingPageCurrent"
        :total-rows="pagingTotalRows"
        :per-page="pagingItemsPerPage"
        pills
        size="sm"
      />

      <!-- List Users -->
      <div v-if="!users.length">
        <h5 class="my-5 py-5 text-center">There are no users.</h5>
      </div>
      <BCard v-else class="mb-3">
        <b-table
          striped
          responsive
          hover
          :items="users"
          :fields="fields"
          :busy="usersQueryInProgress"
          class="browser-table"
          @row-clicked="handleUserDetailRequest"
        >
          <!-- Default data cell scoped slot -->
          <template #cell(name)="data">
            <strong>{{ data.value }}</strong>
          </template>
          <!-- Organization-user ownership/association -->
          <template #cell(organization)="props">
            <span>
              {{
                getActiveUserOrganizationAssociation(props.item)?.organization
                  ?.name || '-'
              }}
            </span>
          </template>
          <!-- Project detail button -->
          <template #cell(id)="data">
            <b-button
              variant="outline-secondary"
              class="btn-sm"
              @click="handleUserDetailRequest(data.item, data.item.id, $event)"
              @click.stop
            >
              <font-awesome-icon :icon="['fa', 'magnifying-glass']" />
            </b-button>
          </template>
        </b-table>
      </BCard>

      <!-- Paging (bellow) -->
      <BPagination
        v-if="pagingTotalRows > pagingItemsPerPage"
        v-model="pagingPageCurrent"
        :total-rows="pagingTotalRows"
        :per-page="pagingItemsPerPage"
        pills
        size="sm"
      />
    </div>
  </div>
</template>

<style scoped></style>
